import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useShareApiService } from "shared/services";
import type { FirebaseLinkOptions } from "shared/services/useShareApiService";
import type { EntitySoccerRoles } from "shared/types";
import { BackendEntityType, EntitySoccerClubRoles } from "shared/types";

import useClipboard from "./useClipboard";
import useToast from "./useToast";

const useShare = () => {
	const { showToast } = useToast();

	const shareService = useShareApiService();

	const { writeToClipboard } = useClipboard();

	const { t } = useTranslation();

	const methods = useMemo(
		() => ({
			copyLink: async ({
				socialMetaTagInfo,
				analyticsInfo,
				dontCopyToClipboard,
				customLink
			}: {
				socialMetaTagInfo: FirebaseLinkOptions["socialMetaTagInfo"];
				analyticsInfo: FirebaseLinkOptions["analyticsInfo"];
				dontCopyToClipboard?: boolean;
				customLink?: string;
			}) => {
				const { shortLink } = await shareService.createFBLink(
					{
						socialMetaTagInfo,
						analyticsInfo
					},
					customLink
				);

				if (shortLink && !dontCopyToClipboard) {
					const copied = await writeToClipboard(shortLink, false);
					if (copied) {
						showToast({ text: "Link copied to clipboard", noIcon: true });
					}
				}

				return shortLink;
			},
			async prepareMessage({
				entityType,
				creatorName
			}: {
				entityType?: BackendEntityType | EntitySoccerRoles;
				creatorName?: string;
			}) {
				let entityTypeName = "";
				switch (entityType) {
					case BackendEntityType.SoccerLeague:
						entityTypeName = t("league");
						break;

					case BackendEntityType.SoccerCompetition:
						entityTypeName = t("competition");
						break;

					case BackendEntityType.SoccerGroup:
						entityTypeName = t("group");
						break;

					case BackendEntityType.SoccerClub:
						entityTypeName = t("club");
						break;

					case BackendEntityType.SoccerReforg:
						entityTypeName = t("ref_org");
						break;

					case BackendEntityType.SoccerFacility:
						entityTypeName = t("facility");
						break;

					case BackendEntityType.CorePerson:
						entityTypeName = t("user");
						break;

					case EntitySoccerClubRoles.PLAYER:
						entityTypeName = t("player");
						break;

					case BackendEntityType.SoccerTeam:
						entityTypeName = t("team");
						break;

					case BackendEntityType.CoreStore:
						entityTypeName = t("store");
						break;
				}

				return `Check out this ${entityTypeName} ${creatorName ? `by ${creatorName}` : "on Tapin Sports"}`;
			}
		}),
		[shareService, showToast, writeToClipboard, t]
	);

	return { ...methods };
};

export default useShare;
